import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useOktaAuth } from "@okta/okta-react";
import { colors } from "../../theme/theme";
import HealthCheck from "../../pages/HealthCheck/HealthCheck";
import NotFound from "../../pages/NotFound/NotFound";
import { Button } from "pepsico-ds";
import {
  setCkms,
  setSnowForm,
} from "../../redux/engagementHistory/engagementHistorySlice";
import { useCanViewCloseOutUnderEngagement } from "../../hooks/access/canViewCloseOutUnderEngagement";
import { validRoutes } from "../../utils/fieldConstants";
import ChatMasterContainer from "./ChatMasterContainer/ChatMasterContainer";
import AppToaster from "../../shared/appToaster";
import SmileyRatings from "../SmileyRatings/SmileyRatings";
const ChatContainer = styled.div<{ isAuthenticated: boolean }>`
  display: ${(props) => (props.isAuthenticated ? "flex" : "none")};
  flex-direction: column;
  // background-color: #ffffff;
  background-color: ${(props) =>
    props.isAuthenticated ? "#ffffff" : "#f3f7fd"};
  padding: 20px 20px 10px 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  margin: 20px;

  /* Fixed dimensions with scroll */
  width: 100%;
  max-width: 1115px; /* Adjust as needed */
  max-height: 600px; /* Adjust as needed */
  overflow: hidden;

  /* Laptops */
  @media (min-width: 1024px) and (max-width: 1366px) {
    margin: 15px;
    max-width: 90%;
    max-height: 100%;
  }

  @media (min-width: 1367px) and (max-width: 1440px) {
    margin: 20px;
    max-width: 90%;
    max-height: 100%;
  }

  /* Desktops */
  @media (min-width: 1441px) and (max-width: 1920px) {
    margin: 25px;
    max-width: 90%;
    max-height: 100%;
  }

  @media (min-width: 1921px) and (max-width: 2560px) {
    margin: 30px;
    max-width: 90%;
    max-height: 100%;
  }

  /* Extra Large Screens */
  @media (min-width: 2561px) {
    margin: 40px;
    max-width: 90%;
    max-height: 100%;
  }
`;

const MessagesArea = styled.div`
  flex-grow: 1;
  overflow: auto; /* This will add scrollbars as needed */
  padding-right: 10px;

  /* Laptops */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding-right: 8px;
  }

  @media (min-width: 1367px) and (max-width: 1440px) {
    padding-right: 12px;
  }

  /* Desktops */
  @media (min-width: 1441px) and (max-width: 1920px) {
    padding-right: 15px;
  }

  @media (min-width: 1921px) and (max-width: 2560px) {
    padding-right: 20px;
  }

  /* Extra Large Screens */
  @media (min-width: 2561px) {
    padding-right: 25px;
  }
`;

const AppContainer = ({ children }: any) => {
  const canViewCloseOutUnderEngagement = useCanViewCloseOutUnderEngagement();
  const { authState } = useOktaAuth();

  const location = useLocation();
  const dispatch = useDispatch();
  const authState1 = useSelector((state: any) => state.auth);
  const { isSurveyFormSubmitted, reschedule_interval } = useSelector(
    (state: any) => state.userAccess.data
  );
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isAuthenticated = !!authState?.accessToken;

  // Debugging the entire state and specific properties
  useEffect(() => {
    console.log("authState1 after redux update:", authState1);

    return () => {
      console.log("Cleaning up debugging logs for authState1");
    };
  }, [authState1]);
  useEffect(() => {
    if (
      isSurveyFormSubmitted === undefined ||
      isSurveyFormSubmitted === null ||
      !reschedule_interval
    )
      return;

    const localAppFeedback =
      localStorage.getItem("isSurveySubmitted") === "true";

    if (isSurveyFormSubmitted || localAppFeedback) {
      setShowFeedbackModal(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const lastShown = localStorage.getItem("feedbackLastShown");
    const now = Date.now();
    if (!lastShown || now - Number(lastShown) > reschedule_interval) {
      setShowFeedbackModal(true);
      localStorage.setItem("feedbackLastShown", now.toString());
    }
    intervalRef.current = setInterval(() => {
      setShowFeedbackModal(true);
      localStorage.setItem("feedbackLastShown", Date.now().toString());
    }, reschedule_interval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [reschedule_interval, isSurveyFormSubmitted]);

  // Destructure values safely (handle cases where data might be missing)
  const { displayName } = authState1;

  const { isSnowForm, isCkms } = useSelector(
    (state: any) => state.engagementData
  );
  const getTitle = () => {
    switch (location.pathname) {
      case "/":
        return "";
      case "/spend-wise-hub":
        return "Consulting Knowledge Management";
      case "/spend-wise-hub/chat-assistant":
        return "Consulting Knowledge Management Chat Assistant";
      case "/spend-wise-hub/upload-engagement_data":
        return "Upload Engagement Data";
      case "/spend-wise-hub/engagement-history":
        return "Engagement History";
      case "/about-chatbot":
        return "About Chatbot";
      case "/user-list":
        return "User Management";
      case "/my-profile":
        return "My Profile";
      default:
        return "Consulting Knowledge Management Chat Assistant";
    }
  };

  const ckmsButtonHandler = () => {
    dispatch(setCkms());
  };
  const snowFormButtonHandler = () => {
    dispatch(setSnowForm());
  };
  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    localStorage.setItem("feedbackLastShown", Date.now().toString());
  };
  return (
    <>
      {authState?.accessToken && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
          }}
        >
          <SmileyRatings
            isModalOpen={showFeedbackModal}
            onModalClose={handleCloseFeedbackModal}
          />
          <div
            className="$font-heading-h5-bold"
            style={{
              padding: "20px 0px 0px 20px",
              color: colors.primary,
              fontSize: "20px",
            }}
          >
            {getTitle()}
          </div>
          <div
            className="$font-heading-h5-medium"
            style={{
              padding: "20px 20px 0px 0px",
              color: colors.primary,
              fontSize: "16px",
            }}
          >
            Welcome, <span style={{ fontWeight: "bold" }}>{displayName}</span>
          </div>
        </div>
      )}
      {authState?.accessToken && getTitle() === "Engagement History" && (
        <>
          {canViewCloseOutUnderEngagement && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 15,
                  padding: "20px 0px 0px 60px",
                  width: "-webkit-fill-available",
                }}
              >
                <Button
                  size="medium"
                  text="CKMS Intake/ Close Out"
                  variant={isCkms ? "primary" : "secondary"}
                  onClick={ckmsButtonHandler}
                />
                <Button
                  size="medium"
                  text="MyServices Intake"
                  variant={isSnowForm ? "primary" : "secondary"}
                  onClick={snowFormButtonHandler}
                />
              </div>
            </>
          )}
        </>
      )}
      {location.pathname.includes("/spend-wise-hub/chat-assistant") ? (
        <ChatMasterContainer>{children}</ChatMasterContainer>
      ) : (
        <ChatContainer isAuthenticated={isAuthenticated}>
          <MessagesArea>{children}</MessagesArea>
        </ChatContainer>
      )}

      {location.pathname === "/" && <></>}
      <AppToaster />
    </>
  );
};

export default AppContainer;
